import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { SENIOR_ADVISORS } from "@/data/seniorAdvisors";

/**
 * /advisors — Senior Advisor profiles page.
 *
 * Dedicated page (mirrors /partnership convention) presenting the full
 * biographies of named Senior Advisors. Each advisor has a hash anchor
 * so the page can be deep-linked (/advisors#alistair-wood) from the
 * homepage Senior Advisors band and the /team compact cards.
 */
const AdvisorsPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid="kr-advisors-page"
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Advisors"
          label="Senior Advisors · Non-Executive · By Engagement"
          title={
            <>
              Sector and regional depth, engaged{" "}
              <span className="italic text-[var(--kr-gold)]">
                non-executively
              </span>
              .
            </>
          }
          description="Senior Advisors engage with the partnership on a non-executive, episodic basis — contributing commercial-banking, structuring, credit-risk and life-sciences pattern recognition to underwriting and portfolio engagement. They are not partners and are not authorised to act on behalf of the firm."
          breadcrumb="Advisors"
        />

        {/* Advisor profiles */}
        <section
          data-testid="kr-advisors-profiles"
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            {/* Tier marker — matches /team & /partnership convention */}
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Senior Advisors
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            <div className="space-y-0 border-t border-[var(--kr-line)]">
              {SENIOR_ADVISORS.map((a) => (
                <article
                  key={a.id}
                  id={a.id}
                  data-testid={`kr-advisors-profile-${a.id}`}
                  className="grid lg:grid-cols-12 gap-10 py-14 border-b border-[var(--kr-line)] scroll-mt-32"
                >
                  {/* Portrait + pillars */}
                  <div className="lg:col-span-4">
                    <div className="relative w-[160px] h-[200px] border border-[var(--kr-line)] overflow-hidden bg-[#0c1018] mb-6">
                      <img
                        src={a.photo}
                        alt={a.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                          filter:
                            "grayscale(40%) contrast(1.05) brightness(0.95) saturate(0.85)",
                          objectPosition: "center 22%",
                        }}
                        loading="lazy"
                      />
                      <span className="absolute top-1.5 left-1.5 w-2.5 h-2.5 border-l border-t border-[var(--kr-gold)]/70" />
                      <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 border-r border-t border-[var(--kr-gold)]/70" />
                      <span className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-l border-b border-[var(--kr-gold)]/70" />
                      <span className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-r border-b border-[var(--kr-gold)]/70" />
                    </div>

                    <div className="border-t border-[var(--kr-line)] max-w-[260px]">
                      {a.pillars.map((p) => (
                        <div
                          key={p.label}
                          className="flex items-baseline justify-between gap-4 py-3 border-b border-[var(--kr-line)]"
                        >
                          <span className="font-mono-label text-[9.5px] text-[var(--kr-text-muted)]">
                            {p.label}
                          </span>
                          <span className="font-serif-display italic text-[var(--kr-text)] text-[12.5px] text-right">
                            {p.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="lg:col-span-8">
                    <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                      {a.role} · {a.qualifier}
                    </div>
                    <h3 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.4rem)] leading-[1.1] mb-6">
                      {a.name}
                    </h3>

                    {a.bio.map((para, i) => (
                      <p
                        key={i}
                        className="text-[var(--kr-text)] text-[15px] leading-[1.85] mb-5"
                      >
                        {para}
                      </p>
                    ))}

                    <div className="flex flex-wrap gap-3 mt-8">
                      <a
                        href={a.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`kr-advisors-linkedin-${a.id}`}
                        className="kr-btn-ghost"
                        style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
                      >
                        LinkedIn ↗
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Closing engagement note */}
        <section
          className="py-20 lg:py-24 border-t border-[var(--kr-line-soft)]"
          style={{ background: "var(--kr-bg-2)" }}
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Engagement
                  </span>
                  <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.2] max-w-[34ch]">
                  Senior Advisors are{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    advisors to the partnership
                  </span>{" "}
                  — not the LP&apos;s contact point.
                </h2>
                <p className="mt-6 text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[64ch]">
                  Senior Advisors are engaged on documented, non-executive
                  terms. They do not act as placement agents, marketing
                  distributors or authorised representatives, and are not
                  the contact point for investor enquiries. Regional investor
                  dialogue is led by the partnership&apos;s{" "}
                  <Link
                    to="/regional-coverage"
                    data-testid="kr-advisors-regional-coverage-link"
                    className="text-[var(--kr-text)] hover:text-[var(--kr-gold)] transition-colors underline underline-offset-4 decoration-[var(--kr-line)]"
                  >
                    Regional Coverage
                  </Link>{" "}
                  tier; limited partners and counterparties are otherwise
                  invited to engage with the partnership directly.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <Link
                  to="/team"
                  data-testid="kr-advisors-team-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Return to Team →
                </Link>
                <Link
                  to="/partnership"
                  data-testid="kr-advisors-partnership-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  The Partnership →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default AdvisorsPage;
